import React, { useState } from 'react';
import { Button } from './ui/Button';
import api from '../lib/api';

interface QueueItem {
  id: string;
  creative_id: string;
  creative_name: string;
  status: string;
}

interface BulkUpdateTestModalProps {
  launchedItems: QueueItem[];
  unlaunchedItems: QueueItem[];
  onClose: () => void;
  onSaved: () => void;
}

const PLATFORM_OPTIONS = ['Facebook', 'TikTok', 'YouTube', 'Instagram', 'Google', 'Other'];

const UNTESTED_REASONS = [
  { value: 'no_budget', label: 'No budget allocated' },
  { value: 'pending_approval', label: 'Waiting for approval' },
  { value: 'wrong_format', label: "Format doesn't match platform" },
  { value: 'duplicate', label: 'Duplicate creative' },
  { value: 'low_priority', label: 'Low priority' },
  { value: 'awaiting_revision', label: 'Needs revision' },
  { value: 'platform_unavailable', label: 'Platform unavailable' },
  { value: 'buyer_unavailable', label: 'No buyer assigned' },
];

export function BulkUpdateTestModal({ launchedItems, unlaunchedItems, onClose, onSaved }: BulkUpdateTestModalProps) {
  const today = new Date().toISOString().split('T')[0];

  // Launched fields
  const [platform, setPlatform] = useState('');
  const [dateTested, setDateTested] = useState(today);
  const [roi, setRoi] = useState('');
  const [ctr, setCtr] = useState('');
  const [hookRate, setHookRate] = useState('');
  const [feedback, setFeedback] = useState('');

  // Not-launched / scheduled action
  const [unlaunchedAction, setUnlaunchedAction] = useState<'skip' | 'not_launched' | 'scheduled'>('skip');
  const [reason, setReason] = useState('');
  const [timeline, setTimeline] = useState('');

  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!platform) {
      alert('Platform is required for launched creatives');
      return;
    }

    const launched = launchedItems.map(item => ({
      id: item.id,
      platform,
      tested_date: dateTested || today,
      roi: roi ? parseFloat(roi) : undefined,
      ctr: ctr ? parseFloat(ctr) : undefined,
      hook_rate: hookRate ? parseFloat(hookRate) : undefined,
      feedback: feedback || undefined,
    }));

    const not_launched: { id: string; untested_reason?: string; timeline?: string; communication_status?: string }[] = [];
    const scheduledItems: { id: string; timeline?: string }[] = [];

    if (unlaunchedAction === 'not_launched') {
      for (const item of unlaunchedItems) {
        not_launched.push({ id: item.id, untested_reason: reason || undefined });
      }
    } else if (unlaunchedAction === 'scheduled') {
      for (const item of unlaunchedItems) {
        scheduledItems.push({ id: item.id, timeline: timeline || undefined });
      }
    }

    setSaving(true);
    try {
      await api.post('/creative-tests/bulk-update', {
        launched,
        not_launched,
        scheduled: scheduledItems,
      });
      onSaved();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-border">
          <h2 className="text-lg font-semibold">Bulk Update</h2>
        </div>

        <div className="p-5 space-y-6">
          {/* Launching section */}
          <div className="border border-green-200 dark:border-green-800 rounded-lg overflow-hidden">
            <div className="bg-green-50 dark:bg-green-900/30 px-4 py-3">
              <h3 className="text-sm font-semibold text-green-800 dark:text-green-300">
                Launching ({launchedItems.length} creative{launchedItems.length !== 1 ? 's' : ''})
              </h3>
              <p className="text-xs text-green-700 dark:text-green-400 mt-0.5">
                {launchedItems.map(i => i.creative_name).join(', ')}
              </p>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1">
                    Platform <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={platform}
                    onChange={e => setPlatform(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground outline-none focus:border-primary"
                  >
                    <option value="">Select...</option>
                    {PLATFORM_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1">Date Tested</label>
                  <input
                    type="date"
                    value={dateTested}
                    onChange={e => setDateTested(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1">ROI</label>
                  <input
                    type="number"
                    step="0.01"
                    value={roi}
                    onChange={e => setRoi(e.target.value)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1">CTR %</label>
                  <input
                    type="number"
                    step="0.01"
                    value={ctr}
                    onChange={e => setCtr(e.target.value)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground outline-none focus:border-primary"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-muted-foreground block mb-1">Hook Rate %</label>
                  <input
                    type="number"
                    step="0.01"
                    value={hookRate}
                    onChange={e => setHookRate(e.target.value)}
                    placeholder="0.00"
                    className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground outline-none focus:border-primary"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground block mb-1">Feedback</label>
                <textarea
                  value={feedback}
                  onChange={e => setFeedback(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground outline-none focus:border-primary resize-none"
                />
              </div>
            </div>
          </div>

          {/* Not launching section */}
          {unlaunchedItems.length > 0 && (
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="bg-muted/40 px-4 py-3">
                <h3 className="text-sm font-semibold text-muted-foreground">
                  Not Launching ({unlaunchedItems.length} creative{unlaunchedItems.length !== 1 ? 's' : ''})
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {unlaunchedItems.map(i => i.creative_name).join(', ')}
                </p>
              </div>
              <div className="p-4 space-y-3">
                <div className="flex flex-col gap-2">
                  {[
                    { value: 'skip', label: 'Leave unchanged (skip)' },
                    { value: 'not_launched', label: 'Mark as Not Launched' },
                    { value: 'scheduled', label: 'Mark as Scheduled' },
                  ].map(({ value, label }) => (
                    <label key={value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="unlaunchedAction"
                        value={value}
                        checked={unlaunchedAction === value}
                        onChange={() => setUnlaunchedAction(value as typeof unlaunchedAction)}
                        className="accent-primary"
                      />
                      <span className="text-sm">{label}</span>
                    </label>
                  ))}
                </div>

                {unlaunchedAction === 'not_launched' && (
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground block mb-1">Reason</label>
                    <select
                      value={reason}
                      onChange={e => setReason(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground outline-none focus:border-primary"
                    >
                      <option value="">Select reason...</option>
                      {UNTESTED_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                  </div>
                )}

                {unlaunchedAction === 'scheduled' && (
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground block mb-1">Timeline</label>
                    <input
                      type="date"
                      value={timeline}
                      onChange={e => setTimeline(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground outline-none focus:border-primary"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="p-5 border-t border-border flex justify-end gap-3">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || launchedItems.length === 0}>
            {saving ? 'Saving...' : 'Save All'}
          </Button>
        </div>
      </div>
    </div>
  );
}
